import {
    ContextualMenuItemType,
    DirectionalHint,
    FocusZone,
    FocusZoneDirection,
    FocusZoneTabbableElements,
    IButton,
    Icon,
    IconButton,
    MessageBarType,
    TooltipHost
} from "@fluentui/react";
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider
} from "@fluentui/react-components";
import { ArrowSyncRegular, CaretDownFilled, CaretRightFilled, InfoRegular, PersonAddRegular, PersonCircleRegular } from '@fluentui/react-icons';
import { observer } from "mobx-react";
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
    BADGE_EXPIRATION_TIME,
    MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS,
    TAGNAME_CHAR_LIMIT,
    TEAM_PICKER_ICON_ID,
    MESSAGE_BAR_AUTO_DISMISS_DELAY_MS
} from "sh-application/../StaffHubConstants";
import setGlobalMessages from "sh-application/actions/setGlobalMessages";
import { fireAccessibilityAlert } from "sh-application/components/accessibilityAlert";
import {
    ScheduleGridUtils,
    setIsReorderTagsPanelOpen,
    setMemberPanelValues,
    toggleTagView,
    updateFilteredTags
} from "sh-application/components/schedules/lib";
import { StaffHubTourMarker } from "sh-application/components/tour/sh-tour/StaffHubTourMarker";
import { appViewState, setHoverColumnHidden } from "sh-application/store";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import DateTimeFormatter, { DateTimeFormatType } from "sh-application/utility/DateTimeFormatter";
import DateUtils from "sh-application/utility/DateUtils";
import InstrumentationUtils from "sh-application/utility/InstrumentationUtils";
import KeyboardUtils from "sh-application/utility/KeyboardUtils";
import TagUtils from "sh-application/utility/TagUtils";
import showConfirmDialog from "sh-confirm-dialog/lib/components/ConfirmDialog";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { ITagEntity } from "sh-models";
import {
    DataServices,
    ECSConfigKey,
    ECSConfigService,
    InstrumentationService,
    StaffHubErrorCodes,
    TagDataService
} from "sh-services";
import StringsStore from "sh-strings/store";
import { TagStore, setIsFocusedTag } from "sh-tag-store";
import { scheduleGenerationEditorState } from "sh-application/components/smartSchedule";
import { smartSchedulePollingState } from "sh-application/components/smartSchedule/store/smartSchedulePollingState";
import { clearSmartSchedulePollingJob } from "sh-application/components/smartSchedule/mutatorActions/pollingActions";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { cancelSmartScheduleGenerationJob, CancelSmartScheduleDialog } from "sh-application/components/smartSchedule";
import { assignOpenShiftsPollingState } from "sh-application/components/autoSchedule/store/assignOpenShiftsPollingState";

import { StaffHubHttpError } from "../../..";
import SpacerItem from "../../commandbar/SpacerItem";
import { PeopleCount } from "../../common/peopleCount/PeopleCount";
import { setMemberDialogOpen, setMemberOpenDialogTriggerButton } from "../../memberEditor/lib";
import schedulesViewStateStore from "../../schedules/lib/store/store";

import type { InputOnChangeData } from "@fluentui/react-components";
import { NewBadge } from "../../common/NewBadge/NewBadge";
import SmartScheduleBadge from "../../common/SmartScheduleBadge/smartScheduleBadge";
import MemberUtils from "sh-application/utility/MemberUtils";
import { TeamStore } from "sh-team-store";
import * as moment from "moment";
import { Button, Input } from "@fluentui/react-components";
import setGlobalMessage from "../../../actions/setGlobalMessage";
import { handleReviewGeneratedSchedule, isOutdatedFailure, mapOverrideToGenerateOption } from "../../smartSchedule/smartScheduleUtils";
import { TagHeaderViewStateStore } from "./lib/store/schema/TagHeaderViewStateStoreSchema";
import { toggleMuteScheduleGroup, UserTeamSettingsStore } from "sh-stores/sh-userteamsettings-store";
import { UserStore } from "sh-stores";

import { RenameGroupDialog } from "./RenameGroupDialog";
import { getDeleteGroupMenuProps, getPeopleCountProps, getRenameGroupMenuProps } from "./TagHeader.mappers";
import { TagHeaderProps } from "./TagHeaderProps";
import MuteScheduleGroupCoachmark from "./MuteScheduleGroupCoachmark";

const styles = require("./NewTagHeader.scss");
const classNames = require("classnames/bind");
const cssExports = require("styles/Exports.scss");

interface ScrollTagHeaderIntoViewRequest {
    tagId?: string;
    groupName?: string;
}

interface ScrollTagHeaderIntoViewOptions extends ScrollIntoViewOptions {
    focus?: boolean;
}

const RETRY_DELAY_MS = 250;

const normalizeGroupName = (value?: string): string => (value || "").trim().toLocaleLowerCase();

function resolveTagHeaderElement(request: ScrollTagHeaderIntoViewRequest): HTMLElement | null {
    if (typeof document === "undefined") {
        return null;
    }

    if (request.tagId) {
        const headerId = calculateTagHeaderElementId(request.tagId);
        const headerElement = document.getElementById(headerId) as HTMLElement;
        if (headerElement) {
            return headerElement;
        }
    }

    return null;
}

function scrollElementIntoView(element: HTMLElement, options?: ScrollIntoViewOptions) {
    element.scrollIntoView({
        behavior: options?.behavior || "smooth",
        block: options?.block || "center",
        inline: options?.inline || "nearest"
    });
}

function scrollLastTagHeaderIntoView(scrollOptions?: ScrollIntoViewOptions): boolean {
    if (typeof document === "undefined" || typeof window === "undefined") {
        return false;
    }

    const headers = Array.from(document.querySelectorAll<HTMLElement>(`.${styles.tagGroupHeader}`));

    if (headers.length > 0) {
        const viewportHeight = window.innerHeight;

        // Scroll past the last header to trigger more rendering
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollTarget = currentScrollTop + viewportHeight; // Scroll one full viewport height down

        window.scrollTo({
            top: scrollTarget,
            behavior: "auto"
        });

        return true;
    } else {
        // No headers found yet, scroll down by viewport height
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollTarget = currentScrollTop + window.innerHeight;

        window.scrollTo({
            top: scrollTarget,
            behavior: "auto"
        });

        return true;
    }
}

function hasReachedWindowBottom(): boolean {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return true;
    }

    const scrollElement = document.scrollingElement || document.documentElement || document.body;
    if (!scrollElement) {
        return true;
    }

    const maxScrollTop = Math.max(0, scrollElement.scrollHeight - window.innerHeight);
    const currentScrollTop = scrollElement.scrollTop || window.pageYOffset || 0;
    return currentScrollTop >= maxScrollTop - 1;
}

export function scrollTagHeaderIntoView(
    request: ScrollTagHeaderIntoViewRequest,
    options?: ScrollTagHeaderIntoViewOptions
): boolean {
    if (typeof document === "undefined") {
        return false;
    }

    if (!request?.tagId && !request?.groupName) {
        return false;
    }

    const { focus, ...scrollOptions } = options || {};
    let attemptCount = 0;
    const MAX_ATTEMPTS = 50;
    let lastScrollHeight = 0;
    let stableHeightCount = 0; // Count how many times height stayed same
    const MAX_STABLE_COUNT = 3; // Only stop after height is stable for 3 checks

    const tryScroll = (): boolean => {
        attemptCount++;

        // Check if element exists now
        const targetElement = resolveTagHeaderElement(request);
        if (targetElement) {
             InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingBannerScrolledToTagLink,
            [
            getGenericEventPropertiesObject(
                InstrumentationService.properties.TagNameLinkClickedOnBanner,
                targetElement.tagName
            ) ]),

            scrollElementIntoView(targetElement, scrollOptions);
            if (focus && typeof targetElement.focus === "function") {
                targetElement.focus();
            }
            return true;
        }

        // Get current scroll height and position
        const currentScrollHeight = document.documentElement?.scrollHeight || document.body?.scrollHeight || 0;

        // Check if scroll height is stable
        if (lastScrollHeight > 0 && currentScrollHeight === lastScrollHeight) {
            stableHeightCount++;
        } else {
            stableHeightCount = 0; // Reset if height changed
        }

        // Stop conditions
        const reachedBottom = hasReachedWindowBottom();
        const noNewContent = stableHeightCount >= MAX_STABLE_COUNT;

        if (attemptCount >= MAX_ATTEMPTS || (reachedBottom && noNewContent)) {
            return false;
        }

        lastScrollHeight = currentScrollHeight;

        // Scroll incrementally to trigger more rendering
        scrollLastTagHeaderIntoView({ ...scrollOptions, behavior: "auto" });

        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                tryScroll();
            }, RETRY_DELAY_MS);
        }

        return false;
    };

    return tryScroll();
}
/**
 * Calculate the element id for a tag group header element.
 * This is used for Aria accessibility label references
 * @param tagId
 */
export function calculateTagHeaderElementId(tagId: string): string {
    return tagId ? `sh-th-${tagId}` : "sh-th-none";
}

/**
 * The header component for groups in Schedule page.
 */
const TagHeader: React.FC<TagHeaderProps> = observer((props: TagHeaderProps) => {
    const [tagHeaderViewState, setTagHeaderViewState] = useState<TagHeaderViewStateStore>({
        tagName: props.tag ? props.tag.name : props.otherGroupTagName,
        tagNameError: ""
    });
    const [isTagEditing, setIsTagEditing] = useState(false);
    const [textWidth, setTextWidth] = useState(0);
    const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
    const [showCancelScheduleDialog, setShowCancelScheduleDialog] = useState(false);

    // Threshold-based loading state for mute schedule group / unmute schedule group feature
    // Hides UI initially, only shows "Loading..." if data takes > 75ms
    // Prevents flicker for fast cache loads (IndexedDB typically 10-50ms, only first load needs network ~100-500ms)
    const [showMuteLoadingUI, setShowMuteLoadingUI] = useState(false);

    const strings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const commonStrings = StringsStore().registeredStringModules.get("common").strings;
    const teamSettingsStrings = StringsStore().registeredStringModules.get("teamsettings").strings;
    const smartScheduleStrings = StringsStore().registeredStringModules.get("smartSchedule").strings;

    const inputRef = useRef<HTMLInputElement>(null);
    const addMember = useRef(null);
    const moreMenuButton = useRef<IButton>(null);
    const moreMenuButtonElement = useRef<HTMLElement>(null);
    const textMeasurementRef = useRef<HTMLSpanElement>(null);

    // Get the actual DOM element from the IButton component for the coachmark
    useEffect(() => {
        if (!moreMenuButtonElement.current) {
            // Query for the button within this tag header's container
            const tagHeaderElement = document.getElementById(tagHeaderElementId);
            if (tagHeaderElement) {
                const automationId = AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader");
                const buttonElement = tagHeaderElement.querySelector(`[data-automation-id="${automationId}"]`) as HTMLElement;
                if (buttonElement) {
                    moreMenuButtonElement.current = buttonElement;
                }
            }
        }
    });

    // Lazy load UserTeamSettings when component mounts (only if feature enabled)
    // Pattern: Load on-demand rather than in bootstrap to avoid ECS config race condition
    // Similar to smart schedule lazy loading pattern
    const teamIdForLoading = TeamStore().teamId;

    useEffect(() => {
        const isMuteFeatureEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableMuteScheduleGroups);

        if (!isMuteFeatureEnabled || !teamIdForLoading) {
            return;
        }

        const isDataLoaded = UserTeamSettingsStore().userTeamSettingsByTeam.get(teamIdForLoading) !== undefined;

        if (!isDataLoaded) {
            // Trigger load - DataService handles caching and deduplication
            DataServices.UserTeamSettingsDataService.getUserTeamSettings(teamIdForLoading)
                .catch(async (error: StaffHubHttpError) => {
                    // Handle errors: distinguish expected 404 from actual errors
                    const isExpected404 = error?.httpErrorCode === 404 &&
                                         error?.staffHubInnerErrorCode === "UserSettingsNotFound";

                    const { setUserTeamSettingsForTeam, setLoadError } = await import("sh-stores/sh-userteamsettings-store");

                    if (isExpected404) {
                        // Expected: Settings don't exist yet (lazy creation pattern)
                        // Store null with no error flag = default behavior (all groups unmuted)
                        setUserTeamSettingsForTeam(teamIdForLoading, null);
                        setLoadError(teamIdForLoading, false);
                    } else {
                        // Actual error: network failure, 500, etc.
                        // Store null with error flag = show error UI
                        setUserTeamSettingsForTeam(teamIdForLoading, null);
                        setLoadError(teamIdForLoading, true);

                        // Log error telemetry for monitoring
                        InstrumentationService.logEvent(InstrumentationService.events.MuteScheduleGroupLoadFailed, [
                            getGenericEventPropertiesObject("teamId", teamIdForLoading),
                            getGenericEventPropertiesObject("errorStatus", error?.httpErrorCode?.toString() || "unknown"),
                            getGenericEventPropertiesObject(InstrumentationService.properties.ErrorMessage, error?.message || "Unknown error")
                        ]);

                        // Show error banner
                        const { default: setGlobalMessage } = await import("sh-application/actions/setGlobalMessage");
                        const { appViewState } = await import("sh-application/store");
                        const strings = StringsStore().registeredStringModules.get("schedulePage")?.strings;

                        if (strings) {
                            const errorMessage = strings.get("userTeamSettingsLoadError");
                            setGlobalMessage(
                                appViewState().globalMessageViewState,
                                errorMessage,
                                MessageBarType.error,
                                null,
                                null,
                                false, // Persist until user dismisses
                                false
                            );
                        }
                    }
                });
        }
    }, [teamIdForLoading]); // Re-run if teamId changes

    // Threshold-based loading UI: Only show "Loading..." if data takes > 75ms
    // RATIONALE: Three-tier caching (Memory → IndexedDB → Network) makes most loads instant
    // - Memory hit: 0ms (synchronous)
    // - IndexedDB hit: 10-50ms (very fast, common on subsequent loads)
    // - Network call: 100-500ms (slow, only on first ever load)
    // Showing loading state immediately would cause unnecessary flicker for cached loads
    //
    // IMPORTANT: Use boolean flag for dependency to avoid reference comparison issues
    // Object references in dependency arrays don't reliably trigger when changing undefined -> null
    const isDataLoadedForEffect = teamIdForLoading
        ? UserTeamSettingsStore().userTeamSettingsByTeam.get(teamIdForLoading) !== undefined
        : false;

    useEffect(() => {
        const isMuteFeatureEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableMuteScheduleGroups);
        if (!isMuteFeatureEnabled) {
            return;
        }

        if (!teamIdForLoading) {
            return;
        }

        if (!isDataLoadedForEffect) {
            // Data not loaded - wait 75ms before showing loading state
            const timeoutId = setTimeout(() => {
                setShowMuteLoadingUI(true);
            }, 75);

            return () => clearTimeout(timeoutId);
        } else {
            // Data loaded - hide loading UI
            setShowMuteLoadingUI(false);
        }
    }, [teamIdForLoading, isDataLoadedForEffect]);

    // New tag header is a tag header that has been created in the current session, so we can show the new badge
    const isNewTagHeader = props.tag?.id ? !!window.sessionStorage.getItem(`tag-${props.tag.id}`) : false;

    // Derived generation state (observer ensures re-render when polling state changes)
    const generatingScheduleState: { state: "idle" | "inProgress" | "success" | "failed", isInitiator?: boolean, primaryMessage?: string, secondaryMessage?: string } = (() => {
        const polling = smartSchedulePollingState();
        // No jobId (undefined/null) => idle immediately.
        if (!polling.jobId) { return { state: "idle" }; }
        const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;
        const thisTagId = props.tag ? props.tag.id : props.otherGroupTagId;
        const statusEntity = polling.statusEntity;
        if (!statusEntity) { return { state: "idle" }; }
        const resultTagId = statusEntity.tagId;
        const isRelevant = (selectedGroupId && selectedGroupId === thisTagId) || (resultTagId && resultTagId === thisTagId);
        if (!isRelevant) { return { state: "idle" }; }
        const initiatorMemberId = statusEntity?.memberId;
        const currentMemberId = MemberUtils.getCurrentUserMemberId();
        const isInitiator = !!initiatorMemberId && initiatorMemberId === currentMemberId;
        const initiatorDisplayName = initiatorMemberId ? TeamStore().members?.get(initiatorMemberId)?.displayName || "" : "";
        const tagName = (thisTagId && TagStore().tags.get(thisTagId)?.name) || thisTagId || "";
        const buildPrimary = (key: string) => smartScheduleStrings.get(key).replace("{0}", tagName);
        const jobStartedTimeIso = (statusEntity as any).jobStartedTime;
        const startMoment = jobStartedTimeIso ? moment(jobStartedTimeIso) : (polling.lastUpdated ? moment(polling.lastUpdated) : null);
        const formattedStartTime = startMoment ? DateTimeFormatter.getDateTimeAsString(startMoment, DateTimeFormatType.DateTime_DateYearShort_Time) : ""; // e.g., "Apr 14, 2026, 2 PM"
        const secondaryMessage = !isInitiator && initiatorDisplayName ? smartScheduleStrings.get("scheduleGenerationInProgressStartedBy").replace("{0}", initiatorDisplayName).replace("{1}", formattedStartTime) : null;
        switch (statusEntity.status) {
            case JobStatus.InProgress:
                return { state: "inProgress", isInitiator, primaryMessage: buildPrimary("scheduleGenerationInProgress"), secondaryMessage };
            case JobStatus.Success:
                // If reviewed, clear polling state to idle so header returns to normal.
                if (statusEntity.jobProperties?.isReviewed) {
                    clearSmartSchedulePollingJob();
                    return { state: "idle" };
                }
                return { state: "success", isInitiator, primaryMessage: isInitiator ? buildPrimary("scheduleGenerationPendingReview") : buildPrimary("generatingScheduleWarningMessage"), secondaryMessage };
            case JobStatus.Failure: {
                    // Treat as idle (suppress stale failure after 1 day)
                    if (isOutdatedFailure((statusEntity as any)?.jobCompletedTime)) {
                        return { state: "idle" };
                    }
                    return { state: "failed", isInitiator };
            }
            default:
                return { state: "idle" };
        }
    })();

    // Clear the session storage after certain time to remove the new badge
    useEffect(() => {
        const timeout = setTimeout(() => {
            if (props.tag?.id) {
                window.sessionStorage.removeItem(`tag-${props.tag.id}`);
            }
        }, BADGE_EXPIRATION_TIME);

        return () => clearTimeout(timeout);
    }, [props.tag?.id]);

    useEffect(() => {
        if (props.tag) {
            const tagName = props.tag.name || commonStrings.get("unnamedGroup");
            setTagHeaderViewState(prevState => ({
                ...prevState,
                tagName: tagName
            }));
        }
    }, [props.tag]);

    // Recalculate width when the tag name changes
    useEffect(() => {
        if (textMeasurementRef.current) {
            setTextWidth(textMeasurementRef.current.offsetWidth);
        }
    }, [tagHeaderViewState.tagName]);

    useEffect(() => {
        if (isConfirmDialogVisible) {
            setHoverColumnHidden(schedulesViewStateStore(), true);
        }
    }, [isConfirmDialogVisible]);

    function calculateWidthOfGroupNameTextField() {
        // Add some padding to the width calculation
        return `${textWidth + 10}px`;
    }

    function getTagId(): string {
        return props.tag ? props.tag.id : props.otherGroupTagId;
    }

    function getTagName(): string {
        return tagHeaderViewState.tagName;
    }

    function getTagHeaderAriaLabel(): string {
        const tagName: string = getTagName();
        return tagName ? tagName : strings.get("emptyTagGroupNameAria");
    }

    function renderFREUIForGroupNameAndHours() {
        const { editEnabled, tag } = props;
        const peopleCountProps = getPeopleCountProps(tag, props.groupData);

        return <>
                {renderCollapseButton()}
                {renderMuteIcon()}
                {renderGroupNameFREUI(editEnabled)}
                {renderGroupHours()}
                {peopleCountProps ? <PeopleCount {...peopleCountProps} /> : null}
                {
                    editEnabled ?
                    <>
                        {SpacerItem.getComponent(true, undefined, true)}
                        {!props.hideActions && renderMoreIcon()}
                    </> : null
                }
        </>;
    }

    function renderGroupNameFREUI(editEnabled: boolean = false) {
        return (
            <div data-automation-id={AutomationUtils.getAutomationId("group", "QAIDGroupName")} data-testid="QAIDGroupName" tabIndex={0} className={styles.tagNameFRE}>
                { renderGroupName() }
            </div>
        );
    }

    const handleSingleClick = () => {
        inputRef.current?.focus();
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupFocusedBySingleClick,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
    };

    const handleDoubleClick = () => {
        setIsTagEditing(true);
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupByDoubleClick,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
    };

    function renderGroupName() {
        return (
            <div
                tabIndex={-1}
                className={styles.groupNameWrapper}
                data-testid="QAIDGroupNameText"
                data-group-name={normalizeGroupName(getTagName())}>
                <div className={styles.groupName}>
                    {getTagName()}
                    {isNewTagHeader && <NewBadge />}
                    {generatingScheduleState.state !== "idle" && generatingScheduleState.isInitiator && (
                        <SmartScheduleBadge
                            message={
                                generatingScheduleState.state === "success"
                                    ? smartScheduleStrings.get("generatedSchedulePendingReviewLabel")
                                    : generatingScheduleState.state === "failed"
                                        ? smartScheduleStrings.get("generatingScheduleFailedLabel")
                                        : smartScheduleStrings.get("generatingScheduleLabel")
                            }
                            intent={generatingScheduleState.state === "success" ? "success" : generatingScheduleState.state === "failed" ? "error" : "info"}
                        />
                    )}
                </div>
            </div>
        );
    }

    function onTagNameTextFieldFocus() {
        setIsFocusedTag(getTagId());
    }

    // tslint:disable-next-line
    function renderGroupNameTextField() {
        const tabIndex = 0;
        return (
            <>
                {/* This hidden span lets us calcualte the width of the input, it helps it grow/shrink */}
                <span
                    ref={textMeasurementRef}
                    style={{
                        position: "absolute",
                        visibility: "hidden",
                        whiteSpace: "nowrap",
                        fontSize: "inherit", // Ensures it uses the same font as the input
                        fontFamily: "inherit", // Ensure same font
                        fontWeight: "inherit", // Ensure same font weight
                        letterSpacing: "inherit" // Same letter spacing
                    }}>
                    {getTagName()}
                </span>
                <Input
                    className={classNames(
                        AutomationUtils.getAutomationId("group", "QAIDGroupNameTextField"),
                        styles.groupNameTextInputField,
                        {
                            [styles.editing]: isTagEditing && !isConfirmDialogVisible
                        }
                    )}
                    data-testid="QAIDGroupNameTextField"
                    ref={inputRef}
                    tabIndex={tabIndex}
                    aria-label={strings.get("groupNameFieldAriaLabel")}
                    maxLength={TAGNAME_CHAR_LIMIT}
                    value={getTagName()}
                    onChange={onTagNameTextFieldChanged}
                    onBlur={onTagNameTextFieldBlur}
                    onFocus={onTagNameTextFieldFocus}
                    onKeyPress={onTagNameTextFieldKeyPress}
                    style={{
                        width: calculateWidthOfGroupNameTextField()
                    }}
                    readOnly={!isTagEditing || !props.editEnabled}
                    onClick={handleSingleClick}
                    onDoubleClick={handleDoubleClick}
                />
                {isNewTagHeader && <NewBadge />}
            </>
        );
    }

    function renderCollapseButton() {
        const viewState = props;
        const collapseButtonAriaLabel = strings.get("collapseTagAriaLabelFRE");
        const expandButtonAriaLabel = strings.get("expandTagAriaLabelFRE");
        const collapseGroupAriaLabel = viewState.isTagCollapsed
            ? expandButtonAriaLabel.format((getTagName() || commonStrings.get("unnamedGroup")))
            : collapseButtonAriaLabel.format((getTagName() || commonStrings.get("unnamedGroup")));

        if (viewState.hideCollapseButton) {
            return null;
        }

        return (
            <span className={styles.collapseIconContainerFRE}>
                {renderCollapseIcon(collapseGroupAriaLabel)}
            </span>
        );
    }

    function renderCollapseIcon(collapseGroupAriaLabel: string) {
        const viewState = props;
        if (viewState.isTagCollapsed) {
            return <CaretRightFilled
                aria-label={ collapseGroupAriaLabel }
                tabIndex={ 0 }
                data-is-focusable='true'
                data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDCollapseIcon") }
                className={ classNames(styles.collapseIconFRE, "no-print") }
                aria-pressed={ viewState.isTagCollapsed }
                role={ "button" }
                onClick={ onTagCollapsed } />;
        } else {
            return <CaretDownFilled
                aria-label={ collapseGroupAriaLabel }
                tabIndex={ 0 }
                data-is-focusable='true'
                data-automation-id={ AutomationUtils.getAutomationId("scheduler", "QAIDCollapseIcon") }
                className={ classNames(styles.collapseIconFRE, "no-print") }
                aria-pressed={ viewState.isTagCollapsed }
                role={ "button" }
                onClick={ onTagCollapsed } />;
        }
    }

    function renderGroupHours() {
        const viewState = props;
        const hoursString = commonStrings.get("hoursShortFormat").format(DateUtils.getHoursRoundedForDisplay(viewState.hours));
        const groupTotalStyle = styles.groupTotalFRE;
        return <div className={ groupTotalStyle }>
            {
                    !viewState.hideGroupTotal &&
                        <span className={ classNames(styles.groupTotalText) }>
                            { hoursString }
                        </span>
                }
            </div>;
    }

    /**
     * Renders mute schedule group icon before group name (bell-on icon = unmuted, bell-off icon = muted)
     *
     * VISIBILITY: Only shown for users with edit permissions (same condition as "..." menu)
     * INTERACTION: onClick intentionally disabled - users should use menu item to build habit
     *
     * LOADING STATES:
     * 1. Not loaded yet (< 75ms): Hidden (prevents flicker for fast cache loads)
     * 2. Loading (> 75ms): Shows "Loading..." with 50% opacity
     * 3. Load error: Shows grayed out with error tooltip (menu item hidden, error banner at top)
     * 4. Loaded: Shows bell (unmuted) or mute (muted) icon
     */
    function renderMuteIcon() {
        const viewState = props;

        if (!TagUtils.shouldShowMuteScheduleGroup(viewState.tag, TeamStore().teamId, props.editEnabled)) {
            return null;
        }

        const teamId = TeamStore().teamId;
        const scheduleGroupId = viewState.tag.id;

        // Check data state: undefined (not loaded), null/entity (loaded), + separate error flag
        // Error flag distinguishes: expected 404 (normal) vs actual error (network failure, 500, etc.)
        const userTeamSettings = UserTeamSettingsStore().userTeamSettingsByTeam.get(teamId);
        const isDataLoaded = userTeamSettings !== undefined;
        const hasLoadError = UserTeamSettingsStore().loadErrorByTeam.get(teamId) ?? false;

        if (!isDataLoaded && !showMuteLoadingUI) {
            // Not loaded + threshold not passed → hide to prevent flicker
            return null;
        }

        let muteScheduleGroupIconName: string;
        let ariaLabel: string;
        let isMuteScheduleGroupIconLoading = false;

        if (!isDataLoaded) {
            // Still loading after 75ms threshold
            muteScheduleGroupIconName = "alert-on-20X20";
            ariaLabel = strings.get("loading");
            isMuteScheduleGroupIconLoading = true;
        } else if (hasLoadError) {
            // Load failed: show grayed out icon with error tooltip
            // UX: Icon shows error state (contextual feedback), menu item enabled for retry, error banner at top (primary feedback)
            muteScheduleGroupIconName = "alert-on-20X20";
            ariaLabel = strings.get("mutePreferenceLoadError");
            isMuteScheduleGroupIconLoading = true;
        } else {
            // Loaded successfully (includes expected 404 "UserSettingsNotFound" = no settings yet, all groups unmuted by default)
            const muteScheduleGroupStatusMap = UserTeamSettingsStore().muteScheduleGroupStatusByTeamAndGroup.get(teamId);
            const isMutedGroup = muteScheduleGroupStatusMap?.get(scheduleGroupId) ?? false;

            const toggleScheduleGroupLoadingMap = UserTeamSettingsStore().toggleLoadingByTeamAndGroup.get(teamId);
            isMuteScheduleGroupIconLoading = toggleScheduleGroupLoadingMap?.get(scheduleGroupId) ?? false;

            muteScheduleGroupIconName = !isMutedGroup ? "alert-on-20X20" : "alert-off-20X20";
            ariaLabel = !isMutedGroup ? strings.get("unmutedScheduleGroupIconTooltip") : strings.get("mutedScheduleGroupIconTooltip");
        }

        return (
            <span className={styles.collapseIconContainerFRE}>
                <Icon
                    iconName={muteScheduleGroupIconName}
                    // onClick intentionally disabled to build user habit of using the menu item
                    // onClick={!isMuteScheduleGroupIconLoading ? onMuteScheduleGroupClicked : undefined}
                    className={classNames(styles.collapseIconFRE, "no-print")}
                    style={{
                        cursor: "default",
                        opacity: isMuteScheduleGroupIconLoading ? 0.5 : 1,
                        transform: "scale(1.5)"
                    }}
                    aria-label={ariaLabel}
                    title={ariaLabel}
                    data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMuteScheduleGroupIcon")}
                />
            </span>
        );
    }

    function autoFocusOnTag() {
        return props.tag && (TagStore().focusedTagId === props.tag.id);
    }

    function onTagNameTextFieldChanged(ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) {
        if (!isConfirmDialogVisible) {
            setIsTagEditing(true);
        }
        setTagHeaderViewState(prevState => ({
            ...prevState,
            tagName: data.value
        }));
    }

    function onTagNameTextFieldBlur() {
        inputRef.current?.blur();
        window.getSelection()?.removeAllRanges(); // Clear text selection
        if (autoFocusOnTag()) {
            setIsFocusedTag("");
        }
        if (tagHeaderViewState.tagName) {
            saveTagName(tagHeaderViewState.tagName);
        } else {
            tagNameEmptyError();
        }
        setIsTagEditing(false);
    }

    function onTagNameTextFieldKeyPress(key: React.KeyboardEvent<HTMLElement>) {
        if (KeyboardUtils.isEnterPressed(key)) {
            key.stopPropagation();
            if (isTagEditing) {
                if (tagHeaderViewState.tagName) {
                    saveTagName(tagHeaderViewState.tagName);
                    InstrumentationService.logEvent(InstrumentationService.events.RenameGroupByKeyboard,
                        [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                            InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
                } else {
                    tagNameEmptyError();
                }
                setTimeout(() => {
                    setIsTagEditing(false);
                }, 0);
            } else {
                setIsTagEditing(true);
            }
        }
    }

    function tagNameEmptyError() {
        setTagHeaderViewState(prevState => ({
            ...prevState,
            tagName: props.tag.name || commonStrings.get("unnamedGroup")
        }));
        setGlobalMessage(
            appViewState().globalMessageViewState,
            strings.get("groupNameEmptyError"),
            MessageBarType.error,
            null,
            null,
            true,
            true,
            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
        );
    }

    async function saveTagName(tagName: string) {
        if (props.tag && (tagName) && (tagName !== props.tag.name)) {
            setTagHeaderViewState(prevState => ({
                ...prevState,
                tagName: tagName
            }));
            const updatedTag = TagUtils.createCloneWithNewTagName(props.tag.id, tagName);
            if (updatedTag) {
                try {
                    let savedTag = await TagDataService.saveTag(updatedTag, false, true, false, false, true);
                    if (savedTag) {
                        updateFilteredTags(savedTag);
                        setGlobalMessage(
                            appViewState().globalMessageViewState,
                            strings.get("groupNameSaved"),
                            MessageBarType.success,
                            null,
                            null,
                            true,
                            true,
                            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
                        );
                        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupSaved, [
                            getGenericEventPropertiesObject(InstrumentationService.properties.StatusSucceeded, "true")
                        ]);
                    }
                } catch (error) {
                    const httpError = error as StaffHubHttpError;
                    if (httpError && httpError.staffHubInnerErrorCode && httpError.staffHubInnerErrorCode === StaffHubErrorCodes.TagNameTaken) {
                        setTagHeaderViewState({
                            tagName: props.tag.name,
                            tagNameError: strings.get("groupNameExistsError")
                        });
                        setGlobalMessage(
                            appViewState().globalMessageViewState,
                            strings.get("groupNameExistsError"),
                            MessageBarType.error,
                            null,
                            null,
                            true,
                            true,
                            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
                        );
                    } else {
                        setTagHeaderViewState({
                            tagName: props.tag.name,
                            tagNameError: strings.get("groupNameError")
                        });
                        setGlobalMessage(
                            appViewState().globalMessageViewState,
                            strings.get("groupNameError"),
                            MessageBarType.error,
                            null,
                            null,
                            true,
                            true,
                            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
                            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
                        );
                    }
                    InstrumentationService.logEvent(InstrumentationService.events.RenameGroupError,
                        [getGenericEventPropertiesObject(InstrumentationService.properties.Error,
                            error.message)]);
                }
            }
        }
    }

    function renderGroupSettingsWithNewFRE() {
        const viewState = props;
        const tabIndex = viewState.isNotFocusable ? -1 : 0;
        const addMemberDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? strings.get("addMemberDisableButtonInYourShifts") : strings.get("addMemberDisableButtonInFilter");
        // Review button appears when generation succeeded (Success or PartialSuccess) for the generated group.
        const pollingStatusEntity = smartSchedulePollingState().statusEntity;
        const isGenerationSuccess = pollingStatusEntity && (pollingStatusEntity.status === JobStatus.Success || pollingStatusEntity.status === JobStatus.PartialSuccess);
        const generatedTagId = pollingStatusEntity?.tagId;
        const currentTagId = props.tag?.id;
        const isSelectedGroup = currentTagId && scheduleGenerationEditorState().selectedGroupId === currentTagId;
        const isResultGroup = currentTagId && generatedTagId === currentTagId;
        // Only the initiating FLM should see the review button and cancel button.
        const isInitiator = generatingScheduleState.isInitiator;
        const showReviewButton = !!isInitiator && !!isGenerationSuccess && (isSelectedGroup || isResultGroup);
        const showCancelButton = !!isInitiator && generatingScheduleState.state === "inProgress";
        const reviewLabel = smartScheduleStrings.get("scheduleGenerationReviewAction");
        const overrideExistingSchedule = pollingStatusEntity?.jobProperties?.overrideExistingSchedule;
        const generateOption = mapOverrideToGenerateOption(overrideExistingSchedule);
        const {startDate: jobStartTime, endDate: jobEndTime} = pollingStatusEntity?.jobProperties || {};
        const jobId = smartSchedulePollingState()?.jobId || "";
        const onReviewClicked = () => handleReviewGeneratedSchedule(
            currentTagId,
            jobStartTime,
            jobEndTime,
            jobId,
            generateOption
        );

        return (
                <div className={ styles.groupSettings }>
                    { showReviewButton && (
                        <Button
                            size="small"
                            appearance="secondary"
                            className={styles.reviewButton}
                            onClick={onReviewClicked}
                        >
                            {reviewLabel}
                        </Button>
                    ) }
                    { showCancelButton && (
                        <Button
                            size="small"
                            appearance="secondary"
                            className={styles.cancelJobButton}
                            onClick={() => {
                                InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingBannerStopSchedulingClicked, [getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, currentTagId), getGenericEventPropertiesObject(InstrumentationService.properties.ClickedatTagLocation, true)]);
                                setShowCancelScheduleDialog(true);
                                }
                            }
                        >
                            {smartScheduleStrings.get("cancelJobButton")}
                        </Button>
                    ) }
                    {
                        TagUtils.isActiveTag(viewState.tag) && !hasActiveAssignJobForThisTag &&
                            <TooltipHost
                                content={ viewState.disableAddMember ? addMemberDisableString : strings.get("addMemberLabel") }
                                hostClassName={ styles.openShiftsInfoTooltipHost }
                                directionalHint={ DirectionalHint.topCenter }
                                calloutProps={ { isBeakVisible: false,
                                gapSpace: parseInt(cssExports.tooltipGapSpace),
                                className: styles.openShiftsInfoCallout } }
                                styles={ {
                                    root: styles.toolTipRoot
                                } }>
                                    <Button
                                        icon={<PersonAddRegular className={
                                            classNames(StaffHubTourMarker.addMember, "no-print", { [styles.disableIcon]: viewState.disableAddMember })
                                        } />}
                                        tabIndex={tabIndex}
                                        disabled={viewState.disableAddMember}
                                        ref={addMember}
                                        appearance="secondary"
                                        onClick={onAddMemberToTagClicked}
                                        aria-label={viewState.disableAddMember ? addMemberDisableString : strings.get("addTeamMemberToTag")}
                                        data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDAddMemberToGroup")}
                                        data-toast-dismiss-trigger="true"
                                    >
                                        {strings.get("addMembersTitle")}
                                    </Button>
                            </TooltipHost>
                    }
                </div>
        );
    }

    function renderMoreIcon() {
        const viewState = props;
        const reorderMemberDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? strings.get("reorderMemberDisableButtonInYourShifts") : strings.get("reorderMemberDisableButtonInFilter");
        const deleteGroupDisableString = ScheduleGridUtils.isYourShiftsView(viewState.employeeViewType) ? strings.get("deleteGroupDisableButtonInYourShifts") : strings.get("deleteGroupDisableButtonInFilter");
        const reorderGroupLabel = viewState.disableReorderGroup ? reorderMemberDisableString : strings.get("reorderTags");
        const deleteGroupLabel = viewState.disableDeleteGroup ? deleteGroupDisableString : strings.get("deleteTag");
        const renameMenuItemProps = getRenameGroupMenuProps();
        const deleteMenuItemProps = getDeleteGroupMenuProps(props);

        /*
         * MUTE SCHEDULE GROUP / UNMUTE SCHEDULE GROUP FEATURE
         *
         * PURPOSE: Allows users to mute notifications for shift requests per schedule group
         * DEFAULT: Users receive notifications for all schedule groups (swap/time off/offers/open shift requests)
         * BEHAVIOR: Muting a schedule group stops notifications for that group's shift requests
         * STORAGE: Mute status stored in UserTeamSettings.mutedScheduleGroupIds
         *
         * MENU ITEM STATES:
         * 1. Not loaded (< 75ms): Hidden (prevents flicker for fast IndexedDB cache loads)
         * 2. Loading (> 75ms): Shows "Loading..." disabled
         * 3. Load error: Hidden (icon shows grayed out, error banner at top provides feedback)
         * 4. Loaded: Shows "Mute group" or "Unmute group" based on current state
         *
         * ERROR HANDLING:
         * - Expected 404 "UserSettingsNotFound": Normal (settings don't exist yet, all groups unmuted by default)
         * - Actual error (500, network failure): Menu item hidden, icon grayed out, error banner shown
         */
        const showMuteMenuItem = TagUtils.shouldShowMuteScheduleGroup(viewState.tag, TeamStore().teamId, props.editEnabled);

        let muteScheduleGroupLabel: string;
        let muteScheduleGroupIconName: string;
        let isMuteScheduleGroupToggleLoading: boolean = false;
        let isMuteScheduleGroupMenuDisabled: boolean = false;
        let muteScheduleGroupClickHandler: (() => void | Promise<void>) = onMuteScheduleGroupClicked;

        if (showMuteMenuItem) {
            const teamId = TeamStore().teamId;
            const scheduleGroupId = viewState.tag.id;

            // Check data state: undefined (not loaded), null/entity (loaded), + separate error flag
            const userTeamSettings = UserTeamSettingsStore().userTeamSettingsByTeam.get(teamId);
            const isDataLoaded = userTeamSettings !== undefined;
            const hasLoadError = UserTeamSettingsStore().loadErrorByTeam.get(teamId) ?? false;

            if (!isDataLoaded && !showMuteLoadingUI) {
                // Not loaded + threshold not passed → hide to prevent flicker
                muteScheduleGroupLabel = undefined;
                muteScheduleGroupIconName = undefined;
            } else if (hasLoadError) {
                // Load error → show retry option
                // Clicking will retry loading UserTeamSettings through the orchestrator
                muteScheduleGroupLabel = strings.get("retryLoadMuteScheduleGroupMenuItem");
                muteScheduleGroupIconName = "Sync"; // Use Sync icon which is sized similarly to other menu icons
                isMuteScheduleGroupMenuDisabled = false;
                muteScheduleGroupClickHandler = onRetryLoadMuteSettings; // Use retry handler instead of toggle
            } else if (!isDataLoaded) {
                // Still loading after 75ms threshold → show disabled loading state
                muteScheduleGroupLabel = strings.get("loading");
                muteScheduleGroupIconName = "alert-on-20X20";
                isMuteScheduleGroupMenuDisabled = true;
            } else {
                // Loaded successfully (includes expected 404 = no settings yet, all unmuted by default)
                const muteScheduleGroupStatusMap = UserTeamSettingsStore().muteScheduleGroupStatusByTeamAndGroup.get(teamId);
                const isMutedGroup = muteScheduleGroupStatusMap?.get(scheduleGroupId) ?? false;

                const toggleScheduleGroupLoadingMap = UserTeamSettingsStore().toggleLoadingByTeamAndGroup.get(teamId);
                isMuteScheduleGroupToggleLoading = toggleScheduleGroupLoadingMap?.get(scheduleGroupId) ?? false;

                muteScheduleGroupLabel = !isMutedGroup ? strings.get("muteScheduleGroupMenuItem") : strings.get("unmuteScheduleGroupMenuItem");
                muteScheduleGroupIconName = !isMutedGroup ? "alert-off-20X20" : "alert-on-20X20";
                isMuteScheduleGroupMenuDisabled = isMuteScheduleGroupToggleLoading;
            }
        }

        // Use Fluent UI v9 Menu when mute feature is enabled
        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableMuteScheduleGroups)) {
            return (
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button
                            appearance="subtle"
                            icon={<Icon iconName="teams-more-svg" />}
                            aria-label={strings.get("moreOptionsLabel")}
                            title={strings.get("moreOptionsLabel")}
                            data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader")}
                            data-testid="QAIDMoreMenuGroupHeader"
                            className={classNames(styles.settingIcon, styles.transparentButton, styles.moreIcon)}
                        />
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {showMuteMenuItem && muteScheduleGroupLabel && (
                                <MenuItem
                                    key="mute-group"
                                    icon={muteScheduleGroupIconName === "Sync" ? <ArrowSyncRegular /> : <Icon iconName={muteScheduleGroupIconName} />}
                                    onClick={muteScheduleGroupClickHandler}
                                    disabled={isMuteScheduleGroupMenuDisabled}
                                    data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMuteScheduleGroupMenuOption")}
                                    data-testid="QAIDMuteScheduleGroupMenuOption"
                                >
                                    {muteScheduleGroupLabel}
                                </MenuItem>
                            )}
                            <MenuItem
                                key="rename-group"
                                icon={<Icon iconName="teams-edit" />}
                                onClick={onRenameGroupClicked}
                                disabled={renameMenuItemProps?.disabled}
                                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDRenameGroupMenuOption")}
                                data-testid="QAIDRenameGroupMenuOption"
                            >
                                {strings.get("renameGroupLabel")}
                            </MenuItem>
                            <MenuItem
                                key="reorder-group"
                                icon={<Icon iconName="teams-drag-drop-offset" />}
                                onClick={onReorderTagsClicked}
                                disabled={viewState.disableReorderGroup}
                                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDReorderGroupMenuOption")}
                                data-testid="QAIDReorderGroupMenuOption"
                            >
                                {strings.get("reorderTags")}
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem
                                key="delete-group"
                                icon={<Icon iconName="teams-delete-svg" />}
                                onClick={onDeleteGroupClicked}
                                disabled={deleteMenuItemProps?.disabled}
                                data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag")}
                                data-testid="QAIDDeleteTag"
                                className="DeleteGroupMenuOption"
                            >
                                {strings.get("deleteTagShort")}
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            );
        }

        // Fallback to Fluent UI v8 IconButton when mute feature is disabled (original order without separator)
        const menuItems = [
            {
                key: "rename-group",
                className: classNames(styles.overflowMenuItem),
                iconProps: {
                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDRename")),
                    iconName: "teams-edit"
                },
                title: renameMenuItemProps?.disabled ? teamSettingsStrings.get("adminPermissionDisabledSettingLabel") : strings.get("renameGroupLabel"),
                ariaLabel: strings.get("renameGroupLabel"),
                text: strings.get("renameGroupLabel"),
                onClick: onRenameGroupClicked,
                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDRenameGroupMenuOption"),
                "data-testid": "QAIDRenameGroupMenuOption",
                ...renameMenuItemProps
            },
            {
                key: "reorder-group",
                className: classNames(styles.overflowMenuItem),
                iconProps: {
                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDReOrder")),
                    iconName: "teams-drag-drop-offset"
                },
                ariaLabel: reorderGroupLabel,
                text: strings.get("reorderTags"),
                title: reorderGroupLabel,
                onClick: onReorderTagsClicked,
                disabled: viewState.disableReorderGroup,
                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDReorderGroupMenuOption"),
                "data-testid": "QAIDReorderGroupMenuOption"
            },
            {
                key: "delete-group",
                className: classNames(styles.overflowMenuItem, "DeleteGroupMenuOption"),
                iconProps: {
                    className: classNames(styles.settingIcon, styles.overflowMenuItemIcon, AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag")),
                    iconName: "teams-delete-svg"
                },
                ariaLabel: deleteGroupLabel,
                title: deleteMenuItemProps?.disabled ? teamSettingsStrings.get("adminPermissionDisabledSettingLabel") : deleteGroupLabel,
                text: strings.get("deleteTag"),
                onClick: onDeleteGroupClicked,
                "data-automation-id": AutomationUtils.getAutomationId("scheduler", "QAIDDeleteTag"),
                "data-testid": "QAIDDeleteTag",
                ...deleteMenuItemProps
            }
        ];

        return <IconButton
                    role="menuitem"
                    title={strings.get("moreOptionsLabel")}
                    ariaLabel={strings.get("moreOptionsLabel")}
                    className={classNames(styles.settingIcon, styles.transparentButton, styles.moreIcon)}
                    styles={{ menuIcon: styles.buttonIcon }}
                    data-automation-id={AutomationUtils.getAutomationId("scheduler", "QAIDMoreMenuGroupHeader")}
                    data-testid="QAIDMoreMenuGroupHeader"
                    componentRef={moreMenuButton}
                    menuIconProps={{ iconName: "teams-more-svg" }}
                    menuProps={{
                        items: menuItems
                    }}
                />;
    }

    function onTagCollapsed() {
        const viewState = props;
        const tagId = getTagId();
        if (tagId) {
            toggleTagView(schedulesViewStateStore(), tagId, !viewState.isTagCollapsed);
        }
    }

    function setFocusOnMoreMenuButton() {
        setTimeout(() => {
            if (moreMenuButton.current) {
                moreMenuButton.current.focus();
            }
        }, 0);
    }

    function setFocusOnTeamPicker() {
        setTimeout(() => {
            const teamPickerIconElement = document.getElementById(TEAM_PICKER_ICON_ID);
                if (teamPickerIconElement) {
                    teamPickerIconElement.focus();
                }
        }, 0);
    }

    function onRenameGroupClicked() {
        InstrumentationService.logEvent(InstrumentationService.events.RenameGroupClicked,
            [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
        setIsConfirmDialogVisible(true);
    }

    const onReorderTagsClicked = () => {
        InstrumentationService.logEvent(InstrumentationService.events.ReorderTagsClicked, [
            getGenericEventPropertiesObject(
                InstrumentationService.properties.CurrentView,
                InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType)
            )
        ]);
        setIsReorderTagsPanelOpen(schedulesViewStateStore(), true);
    };

    const onAddMemberToTagClicked = () => {
        if (props.tag) {
            setMemberDialogOpen(true);
            setMemberOpenDialogTriggerButton(addMember.current ?? null);
            setMemberPanelValues(schedulesViewStateStore(), props.tag.id, null);
            InstrumentationService.logEvent(InstrumentationService.events.GroupAddMemberClicked);
        }
    };

    const setGlobalErrorMessage = (groupName: string) => {
        setGlobalMessages(appViewState().globalMessageViewState, [strings.get("groupNotDeleted").format(groupName)], MessageBarType.error);
    };

    const onDeleteGroupClicked = () => {
        const viewState = props;
        if (viewState.tag) {
            InstrumentationService.logEvent(InstrumentationService.events.GroupDeleteOnScheduleClicked,
                [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);

            const groupName = viewState.tag.name || commonStrings.get("unnamedGroup");

            showConfirmDialog(
                strings.get("deleteTag"),
                viewState.hasShifts
                    ? strings.get("deleteGroupMessageWithShifts").format(groupName)
                    : strings.get("deleteGroupMessage").format(groupName),
                {
                    okText: viewState.hasShifts ? "" : commonStrings.get("remove"),
                    isBlocking: true,
                    containerClassName: styles.confirmDialog
                },
                () => {
                    if (!viewState.hasShifts) {
                        try {
                            InstrumentationService.logEvent(InstrumentationService.events.GroupCommitDeleteOnScheduleClicked,
                                [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView, InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
                            TagDataService.deleteTag(viewState.tag)
                                .then((tag: ITagEntity) => {
                                    const addGroupElement = document.getElementById("addGroup");
                                    if (addGroupElement) {
                                        addGroupElement.focus();
                                    }
                                    if (tag) {
                                        fireAccessibilityAlert(strings.get("groupDeleted").format(groupName));
                                    } else {
                                        setGlobalErrorMessage(groupName);
                                    }
                                });
                        } catch (error) {
                            setGlobalErrorMessage(groupName);
                        }
                        setFocusOnTeamPicker();
                    }
                },
                () => {
                    setFocusOnMoreMenuButton();
                },
                false,
                false,
                true
            );
        }
    };

    /**
     * Retry loading UserTeamSettings when there's a load error.
     * This is a pure retry - doesn't toggle mute/unmute state.
     * If load succeeds, UI updates to show correct mute/unmute menu item.
     */
    const onRetryLoadMuteSettings = async () => {
        const teamId = TeamStore().teamId;
        if (!teamId) {
            return;
        }

        try {
            // Retry loading UserTeamSettings (uses three-tier cache)
            await DataServices.UserTeamSettingsDataService.getUserTeamSettings(teamId);

            // Success - clear error state now that data loaded successfully
            const { setLoadError } = await import("sh-stores/sh-userteamsettings-store");
            setLoadError(teamId, false);

            // UI will update automatically to show correct mute/unmute menu item
            // No toggle happens - user can now see actual state
        } catch (error) {
            // Load failed again - handle same as initial load error
            const httpError = error as StaffHubHttpError;
            const isExpected404 = httpError?.httpErrorCode === 404 &&
                                 httpError?.staffHubInnerErrorCode === "UserSettingsNotFound";

            const { setUserTeamSettingsForTeam, setLoadError } = await import("sh-stores/sh-userteamsettings-store");

            if (isExpected404) {
                // Expected: Settings don't exist yet (all groups unmuted by default)
                setUserTeamSettingsForTeam(teamId, null);
                setLoadError(teamId, false);
            } else {
                // Actual error: network failure, 500, etc.
                setUserTeamSettingsForTeam(teamId, null);
                setLoadError(teamId, true);

                // Log retry failure telemetry
                InstrumentationService.logEvent(InstrumentationService.events.MuteScheduleGroupLoadFailed, [
                    getGenericEventPropertiesObject("teamId", teamId),
                    getGenericEventPropertiesObject("errorStatus", httpError?.httpErrorCode?.toString() || "unknown"),
                    getGenericEventPropertiesObject(InstrumentationService.properties.ErrorMessage, httpError?.message || "Retry failed"),
                    getGenericEventPropertiesObject("isRetry", true)
                ]);

                // Show error banner with specific mute setting error message
                const { default: setGlobalMessages } = await import("sh-application/actions/setGlobalMessages");
                const { appViewState } = await import("sh-application/store");

                const errorMessage = strings.get("muteScheduleGroupLoadErrorMessage");
                setGlobalMessages(
                    appViewState().globalMessageViewState,
                    [errorMessage],
                    MessageBarType.error,
                    null,
                    null,
                    false,
                    true
                );
            }
        }
    };

    const onMuteScheduleGroupClicked = () => {
        const viewState = props;
        if (viewState.tag && TeamStore().teamId) {
            const teamId = TeamStore().teamId;
            const scheduleGroupId = viewState.tag.id;

            // Toggle mute/unmute immediately
            // Orchestrator handles confirmation dialog and business logic
            toggleMuteScheduleGroup(teamId, scheduleGroupId);
        }
    };

    const tagHeaderElementId = calculateTagHeaderElementId(getTagId());
    const ariaProps: AriaProperties = {
        role: AriaRoles.rowheader,
        label: getTagHeaderAriaLabel()
    };
    const shouldShowOtherFLMMessage = (generatingScheduleState.state === "inProgress" || generatingScheduleState.state === "success") && !generatingScheduleState.isInitiator;
    const showInitiatorGeneratingWarning = generatingScheduleState.state === "inProgress" && generatingScheduleState.isInitiator;

    // Is this tag the target of an in-flight assign-open-shifts job? The visible
    // progress UI is now a global top-right toast (rendered by AssignOpenShiftsSuccessToast),
    // but we still gate this header's per-tag controls (e.g. Add Member) on the active job.
    const thisTagId = props.tag ? props.tag.id : props.otherGroupTagId;
    const assignJob = assignOpenShiftsPollingState().activeJob;
    const hasActiveAssignJobForThisTag = !!assignJob && assignJob.tagId === thisTagId &&
        (assignJob.status === JobStatus.InProgress || assignJob.status === JobStatus.Queued);

    // Extract fresShownSoFar at component level so MobX can track changes (same pattern as AutoScheduleButton)
    const fresShownSoFar = UserStore().userSettings?.fresShownSoFar || [];

    const renderTagHeaderFocusZone = (): JSX.Element => {
        if (ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) && MemberUtils.isAdmin(TeamStore() && TeamStore().me)) {
            return <FocusZone
                isCircularNavigation={false}
                handleTabKey={FocusZoneTabbableElements.all}
                direction={FocusZoneDirection.horizontal}
                id={tagHeaderElementId}
                data-tag-id={getTagId()}
                className={styles.tagGroupHeaderWrapper}
                {...generateDomPropertiesForAria(ariaProps)}
            >
                <div className={styles.tagGroupHeader}>
                    {renderFREUIForGroupNameAndHours()}
                    {props.tag && props.editEnabled && !props.hideActions && renderGroupSettingsWithNewFRE()}
                </div>
                {shouldShowOtherFLMMessage && generatingScheduleState.primaryMessage && (
                    <div className={styles.generatingScheduleRow}>
                        <PersonCircleRegular className={styles.infoIcon} aria-hidden={true} style={{ width: '20px', height: '20px' }} />
                        <span className={styles.generatingScheduleSecondary}>
                            {[
                                generatingScheduleState?.secondaryMessage?.trim(),
                                generatingScheduleState?.primaryMessage?.trim()
                                ]
                                .filter(Boolean)
                                .join('. ')}
                        </span>
                    </div>
                )}
                {showInitiatorGeneratingWarning && (
                    <div className={styles.generatingScheduleRow}>
                        <InfoRegular className={styles.infoIcon} aria-hidden={true} style={{ width: '20px', height: '20px' }}/>
                        <span className={styles.generatingScheduleWarning}>{smartScheduleStrings.get("generatingScheduleWarningMessage")}</span>
                    </div>
                )}
            </FocusZone>;
        } else {
            return <FocusZone
                isCircularNavigation={false}
                handleTabKey={FocusZoneTabbableElements.all}
                direction={FocusZoneDirection.horizontal}
                id={tagHeaderElementId}
                data-tag-id={getTagId()}
                className={styles.tagGroupHeader}
                {...generateDomPropertiesForAria(ariaProps)}
            >
                    {renderFREUIForGroupNameAndHours()}
                    {props.tag && props.editEnabled && !props.hideActions && renderGroupSettingsWithNewFRE()}
            </FocusZone>;
        }
    };

    return props.tag && (
        <>
                { isConfirmDialogVisible &&
                    <RenameGroupDialog
                        editEnabled={ props.editEnabled }
                        oldTagName={ props.tag.name  || commonStrings.get("unnamedGroup") }
                        tagName={ getTagName() }
                        saveTagName={ saveTagName }
                        setFocusOnMoreMenuButton={ setFocusOnMoreMenuButton }
                        setIsConfirmDialogVisible={ setIsConfirmDialogVisible }
                        setTagHeaderViewState={ setTagHeaderViewState }
                        scheduleCalendarType={ props.scheduleCalendarType }
                        isNotFocusable={ props.isNotFocusable }
                        tag={ props.tag }
                        key={ "RenameGroupDialog" }
                    />
                }
                {renderTagHeaderFocusZone()}
                <CancelSmartScheduleDialog
                    isOpen={showCancelScheduleDialog}
                    onDismiss={() => setShowCancelScheduleDialog(false)}
                    onConfirm={() => {
                        setShowCancelScheduleDialog(false);
                        try { cancelSmartScheduleGenerationJob({ teamId: TeamStore().teamId }); } catch { /* swallow */ }
                    }}
                />
                {/* Render coachmark for the first schedule group (only when feature is enabled) */}
                {TagUtils.shouldShowMuteScheduleGroup(props.tag, TeamStore().teamId, props.editEnabled) && (
                    <MuteScheduleGroupCoachmark
                        targetRef={moreMenuButtonElement}
                        isFirstGroup={props.isFirstGroup ?? false}
                        tag={props.tag}
                        fresShownSoFar={fresShownSoFar}
                    />
                )}
            </>
    );
});

export default TagHeader;